/**
 * Shared helpers for ForaVida inmeettool calculators.
 * Built as a standalone IIFE, exposes window.FviHelpers.
 *
 * @package ForaVida_Inmeettool
 */
(function () {
	'use strict';

	function ready( fn ) {
		if ( document.readyState === 'loading' ) {
			document.addEventListener( 'DOMContentLoaded', fn, { once: true } );
		} else {
			fn();
		}
	}

	function showError( root, msg ) {
		var resultEl = root.querySelector( '[data-fvi="result"]' );
		if ( resultEl ) {
			resultEl.innerHTML = '<div class="fvi-warn" role="alert">' + msg + '</div>';
		}
	}

	function showNotice( root, msg ) {
		var resultEl = root.querySelector( '[data-fvi="result"]' );
		if ( ! resultEl ) { return; }
		var existing = root.querySelector( '[data-fvi-notice]' );
		if ( existing ) { existing.remove(); }
		var el = document.createElement( 'div' );
		el.className = 'fvi-ok';
		el.setAttribute( 'role', 'status' );
		el.setAttribute( 'aria-live', 'polite' );
		el.setAttribute( 'data-fvi-notice', '1' );
		el.textContent = msg;
		resultEl.parentNode.insertBefore( el, resultEl.nextSibling );
		setTimeout( function () { el.remove(); }, 2500 );
	}

	function fallbackCopy( root, text, t ) {
		var ta = document.createElement( 'textarea' );
		ta.value = text;
		ta.style.position = 'fixed';
		ta.style.opacity  = '0';
		document.body.appendChild( ta );
		ta.select();
		try {
			document.execCommand( 'copy' );
			showNotice( root, t( 'copied' ) );
		} catch ( e ) {
			showError( root, t( 'copyFailed' ) );
		}
		document.body.removeChild( ta );
	}

	function copyToClipboard( root, t, opts ) {
		var resultEl = root.querySelector( '[data-fvi="result"]' );
		if ( resultEl.querySelector( '.fvi-placeholder' ) ) {
			showError( root, t( 'calcFirst' ) );
			return;
		}
		var text = resultEl.innerText;
		if ( ! text || ! text.trim() ) {
			showError( root, t( 'calcFirst' ) );
			return;
		}
		if ( opts && opts.normalizeNewlines ) {
			text = text.replace( /\n{2,}/g, '\n' );
		}
		if ( navigator.clipboard && navigator.clipboard.writeText ) {
			navigator.clipboard.writeText( text ).then(
				function () { showNotice( root, t( 'copied' ) ); },
				function () { fallbackCopy( root, text, t ); }
			);
		} else {
			fallbackCopy( root, text, t );
		}
	}

	function printContent( root, t, printCSS ) {
		var resultEl = root.querySelector( '[data-fvi="result"]' );
		if ( resultEl.querySelector( '.fvi-placeholder' ) ) {
			showError( root, t( 'calcFirst' ) );
			return;
		}
		var content = resultEl.innerHTML;
		if ( ! content || ! content.trim() ) { return; }
		// WP emits BCP-47 on <html lang> via language_attributes() — reuse it so
		// the print window declares the correct language for assistive tech.
		var pageLang = ( document.documentElement.lang || 'nl' );
		var html =
			'<!doctype html><html lang="' + pageLang + '"><head><meta charset="utf-8">' +
			'<title>' + t( 'printPageTitle' ) + '</title>' +
			'<style>' +
			'body{font-family:Inter,system-ui,sans-serif;margin:20px;color:#1b1c12}' +
			'h1{font-size:18px;font-weight:700;color:#3d441e;margin:0 0 16px}' +
			'.fvi-ok{background:#f0eede;border-left:4px solid #6F7C4B;padding:10px 12px;border-radius:6px;margin-top:8px;font-size:12px}' +
			'.fvi-warn{background:#f0eede;border-left:4px solid #B08947;padding:10px 12px;border-radius:6px;margin-top:8px;font-size:12px}' +
			'.fvi-badge{font-weight:700;color:#3d441e}' +
			'.fvi-card__header,.fvi-card__actions,.fvi-btn--icon,.fvi-color-section{display:none}' +
			( printCSS || '' ) +
			'</style></head>' +
			'<body><h1>' + t( 'printHeading' ) + '</h1>' +
			content +
			'</body></html>';
		var w = window.open( '', 'printWindow' );
		w.document.open();
		w.document.write( html );
		w.document.close();
		w.focus();
		w.print();
		w.close();
	}

	function debounceObserver( fn, delay ) {
		var timer = null;
		new MutationObserver( function () {
			if ( timer ) return;
			timer = setTimeout( function () {
				timer = null;
				fn();
			}, delay || 100 );
		} ).observe( document.documentElement, { childList: true, subtree: true } );
	}

	// Number formatting follows <html lang>, so nl-NL keeps `.` as thousand
	// separator, de-DE matches, en-US flips to `,`. No fallback needed —
	// Intl.NumberFormat normalises unknown tags to the default locale.
	var pageLocale = ( document.documentElement.lang || 'nl-NL' );
	var fmt = function ( n ) {
		return new Intl.NumberFormat( pageLocale ).format( Math.round( n ) );
	};
	var nf1 = function ( n ) {
		return ( Math.round( n * 10 ) / 10 ).toFixed( 1 );
	};
	var nf2 = function ( n ) {
		return ( Math.round( n * 100 ) / 100 ).toFixed( 2 );
	};

	var ICON_PRINT = '<svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>';
	var ICON_COPY  = '<svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';

	window.FviHelpers = {
		ready: ready,
		showError: showError,
		showNotice: showNotice,
		copyToClipboard: copyToClipboard,
		printContent: printContent,
		debounceObserver: debounceObserver,
		fmt: fmt,
		nf1: nf1,
		nf2: nf2,
		ICON_PRINT: ICON_PRINT,
		ICON_COPY: ICON_COPY
	};
})();
